import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api";
import { STATE_STYLES, relativeTime } from "@/lib/format";

const POLL_MS = 15000;

function fmtAge(s) {
  if (s == null) return "no data";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

function Tile({ s }) {
  const style = STATE_STYLES[s.state];
  return (
    <div
      data-testid={`public-tile-${s.id}`}
      className={`relative flex-1 flex flex-col justify-between p-8 sm:p-12 border ${style.border} ${style.bg} transition-colors duration-150`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs sm:text-sm uppercase tracking-[0.25em] text-zinc-400 mb-1">
            {s.kind}
          </div>
          <div className="font-display text-2xl sm:text-4xl font-black uppercase tracking-tight">
            {s.name}
          </div>
        </div>
        <span
          data-testid={`public-state-${s.id}`}
          className={`text-[10px] sm:text-xs uppercase tracking-widest border px-3 py-1 ${style.pill}`}
        >
          {style.label}
        </span>
      </div>

      <div className="flex items-baseline gap-3 my-8">
        <span
          data-testid={`public-temp-${s.id}`}
          className={`font-display font-black tracking-tighter leading-none text-[26vw] sm:text-[16vw] lg:text-[12vw] ${style.text}`}
        >
          {s.temp_f != null ? s.temp_f.toFixed(1) : "—"}
        </span>
        <span className="font-display font-bold text-3xl sm:text-5xl text-zinc-500">
          °F
        </span>
      </div>

      <div
        data-testid={`public-age-${s.id}`}
        className="text-sm sm:text-base text-zinc-400 uppercase tracking-widest"
      >
        {s.state === "no_data"
          ? "no data yet"
          : s.state === "stale"
            ? `stale · ${fmtAge(s.age_seconds)}`
            : fmtAge(s.age_seconds)}
      </div>
    </div>
  );
}

export default function PublicStatus() {
  const [data, setData] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [error, setError] = useState("");

  const fetchStatus = useCallback(async () => {
    try {
      const { data: payload } = await api.get("/public/status");
      setData(payload);
      setLastFetched(new Date());
      setError("");
    } catch (err) {
      setError(err?.message || "Failed to load status");
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, POLL_MS);
    return () => clearInterval(t);
  }, [fetchStatus]);

  return (
    <div
      data-testid="public-status-page"
      className="min-h-screen w-full bg-zinc-950 text-zinc-100 flex flex-col font-sans"
    >
      <header
        className="p-4 sm:p-6 flex items-center justify-between border-b border-zinc-800"
        style={{ backgroundColor: "var(--purely-navy)" }}
      >
        <div className="flex items-center gap-3">
          <img
            src={require("../assets/purely_it_logo.png")}
            alt="Purely IT Temp Monitor"
            className="h-10 w-auto"
          />
          <div className="font-display text-xl sm:text-2xl font-black uppercase tracking-tight hidden sm:block">
            Cold Chain Status
          </div>
        </div>
        <div className="text-[10px] sm:text-xs uppercase tracking-widest text-zinc-500">
          {data?.server_time
            ? `Server ${new Date(data.server_time).toLocaleTimeString()}`
            : "—"}
        </div>
      </header>

      {error && (
        <div className="p-4 text-red-400 text-sm" data-testid="public-error">
          {error}
        </div>
      )}

      <div className="flex-1 flex flex-col lg:flex-row">
        {(data?.sensors || []).map((s) => (
          <Tile key={s.id} s={s} />
        ))}
      </div>

      <footer
        data-testid="public-footer"
        className="p-3 sm:p-4 border-t border-zinc-800 flex items-center justify-between text-[10px] sm:text-xs uppercase tracking-widest text-zinc-500"
      >
        <div data-testid="public-last-refreshed">
          Last refreshed:{" "}
          {lastFetched ? relativeTime(lastFetched.toISOString()) : "—"} ·
          auto-refresh {POLL_MS / 1000}s
        </div>
        <Link
          to="/login"
          data-testid="public-staff-login-link"
          className="hover:text-zinc-300 transition-colors"
        >
          Staff login →
        </Link>
      </footer>
    </div>
  );
}
